import { z } from 'zod';
import { ProcedureIdSchema, SignatureDataUrlSchema, UserIdSchema } from './common';

/**
 * Datos capturados al firmar el consentimiento informado.
 * El backend genera el PDF a partir de estos campos + plantilla institucional.
 *
 * Regla de firmas del paciente:
 *   - Lo normal es que el propio paciente firme (`firmaPaciente`).
 *   - Si el paciente está físicamente impedido (sedación previa, etc.),
 *     puede firmar un acompañante autorizado (`firmaAcompanante` +
 *     `nombreAcompanante` + `parentescoAcompanante`).
 *   - Al menos una de las dos firmas DEBE estar presente — lo valida el
 *     `.superRefine` al final del schema.
 */
export const ConsentSchema = z
  .object({
    procedimientoId: ProcedureIdSchema,
    riesgosExplicados: z.boolean(),
    alternativasExplicadas: z.boolean(),
    preguntasResueltas: z.boolean(),
    observaciones: z.string().max(2000).optional(),
    firmaPaciente: SignatureDataUrlSchema.optional(),
    firmaAcompanante: SignatureDataUrlSchema.optional(),
    nombreAcompanante: z.string().min(2).max(150).optional(),
    parentescoAcompanante: z.string().min(2).max(60).optional(),
    firmaMedicoId: UserIdSchema,
    // Opcional: vacío si el médico tiene `users.firmaUrl` pre-cargada — en
    // ese caso el generador de PDF embebe la firma escaneada del usuario.
    firmaMedico: SignatureDataUrlSchema.optional(),
    firmadoEn: z.string().datetime(),
  })
  .superRefine((data, ctx) => {
    const tienePaciente = data.firmaPaciente !== undefined && data.firmaPaciente !== '';
    const tieneAcompanante =
      data.firmaAcompanante !== undefined && data.firmaAcompanante !== '';
    if (!tienePaciente && !tieneAcompanante) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['firmaPaciente'],
        message:
          'Debe firmar el paciente o un acompañante autorizado.',
      });
    }
    if (tieneAcompanante) {
      if (data.nombreAcompanante === undefined || data.nombreAcompanante.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['nombreAcompanante'],
          message: 'Nombre del acompañante obligatorio cuando firma él.',
        });
      }
      if (data.parentescoAcompanante === undefined || data.parentescoAcompanante.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['parentescoAcompanante'],
          message: 'Parentesco del acompañante obligatorio cuando firma él.',
        });
      }
    }
  });
export type Consent = z.infer<typeof ConsentSchema>;

// `.omit()` no es soportado tras un `.superRefine()` — replicamos manualmente
// la forma sin `firmadoEn` y reaplicamos las mismas reglas de validación.
export const SignConsentSchema = z
  .object({
    procedimientoId: ProcedureIdSchema,
    riesgosExplicados: z.boolean(),
    alternativasExplicadas: z.boolean(),
    preguntasResueltas: z.boolean(),
    observaciones: z.string().max(2000).optional(),
    firmaPaciente: SignatureDataUrlSchema.optional(),
    firmaAcompanante: SignatureDataUrlSchema.optional(),
    nombreAcompanante: z.string().min(2).max(150).optional(),
    parentescoAcompanante: z.string().min(2).max(60).optional(),
    firmaMedicoId: UserIdSchema,
    // Opcional: si el médico tiene `firmaUrl` pre-cargada en `users`, el
    // backend usa esa firma y el frontend NO captura una nueva en tablet.
    // Si el médico NO tiene firma pre-cargada, este campo se vuelve
    // obligatorio (validación reforzada en el backend).
    firmaMedico: SignatureDataUrlSchema.optional(),
  })
  .superRefine((data, ctx) => {
    const tienePaciente = data.firmaPaciente !== undefined && data.firmaPaciente !== '';
    const tieneAcompanante =
      data.firmaAcompanante !== undefined && data.firmaAcompanante !== '';
    if (!tienePaciente && !tieneAcompanante) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['firmaPaciente'],
        message: 'Debe firmar el paciente o un acompañante autorizado.',
      });
    }
    if (tieneAcompanante) {
      if (data.nombreAcompanante === undefined || data.nombreAcompanante.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['nombreAcompanante'],
          message: 'Nombre del acompañante obligatorio cuando firma él.',
        });
      }
      if (data.parentescoAcompanante === undefined || data.parentescoAcompanante.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['parentescoAcompanante'],
          message: 'Parentesco del acompañante obligatorio cuando firma él.',
        });
      }
    }
  });
export type SignConsentInput = z.infer<typeof SignConsentSchema>;
